import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';
import { getHiddenForecastAccounts, normalizeForecastAccountName } from '../utils/forecastVisibility';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

function Accounts({ onboardingMonth = '' }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [accountForecast, setAccountForecast] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [hiddenForecastAccounts, setHiddenForecastAccounts] = useState([]);

  const user = localStorage.getItem('loggedInUsername');
  const currentYear = new Date().getFullYear();

  const minForecastYear = useMemo(() => {
    const normalized = String(onboardingMonth || '').trim();
    const yearValue = Number.parseInt(normalized.slice(0, 4), 10);
    if (Number.isNaN(yearValue)) {
      return currentYear;
    }
    return yearValue;
  }, [onboardingMonth, currentYear]);

  const maxForecastYear = useMemo(() => Math.max(minForecastYear, currentYear + 1), [minForecastYear, currentYear]);

  useEffect(() => {
    if (year < minForecastYear) {
      setYear(minForecastYear);
      return;
    }
    if (year > maxForecastYear) {
      setYear(maxForecastYear);
    }
  }, [year, minForecastYear, maxForecastYear]);

  const loadAccountForecast = async (token, yearValue) => {
    const response = await fetch('/api/accounting/forecast/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        user,
        year: yearValue
      })
    });

    if (!response.ok) {
      throw new Error('Failed to load account forecast');
    }

    return response.json();
  };

  const refreshAccountForecast = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const data = await loadAccountForecast(token, year);
      setAccountForecast(Array.isArray(data.forecast) ? data.forecast : []);
      setStatusMessage('');
    } catch (error) {
      setAccountForecast([]);
      setStatusMessage('Unable to load account forecast.');
    }
  };

  useEffect(() => {
    refreshAccountForecast();
  }, [year]);

  useEffect(() => {
    setHiddenForecastAccounts(getHiddenForecastAccounts(user));
  }, [user]);

  const accountColumns = useMemo(() => {
    const names = new Set();
    accountForecast.forEach((row) => {
      Object.keys(row.balances || {}).forEach((name) => names.add(name));
    });
    return Array.from(names);
  }, [accountForecast]);

  const visibleAccountColumns = useMemo(() => {
    const hidden = new Set(hiddenForecastAccounts);
    return accountColumns.filter((name) => !hidden.has(normalizeForecastAccountName(name)));
  }, [accountColumns, hiddenForecastAccounts]);

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Account Forecasts</h2>
            <p className="section-subtitle">Forecasted month-by-month balances for all accounts.</p>
          </div>
          <label>
            Year
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              min={minForecastYear}
              max={maxForecastYear}
            />
          </label>
        </div>

        <section className="panel">
          <h3>Forecast Overview</h3>
          <p className="panel-sub">This section only shows account forecasts.</p>
          {statusMessage && <div className="empty-state">{statusMessage}</div>}
          {!statusMessage && accountForecast.length === 0 && (
            <div className="empty-state">No account forecast available yet.</div>
          )}
          {!statusMessage && accountForecast.length > 0 && accountColumns.length === 0 && (
            <div className="empty-state">No account balances found in forecast data.</div>
          )}
          {!statusMessage && accountForecast.length > 0 && accountColumns.length > 0 && visibleAccountColumns.length === 0 && (
            <div className="empty-state">All accounts are hidden in forecast settings.</div>
          )}
          {!statusMessage && accountForecast.length > 0 && visibleAccountColumns.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  {visibleAccountColumns.map((name) => (
                    <th key={name}>{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accountForecast.map((row) => (
                  <tr key={row.month_key || row.month}>
                    <td>{row.month || row.month_key}</td>
                    {visibleAccountColumns.map((name) => (
                      <td key={`${row.month_key || row.month}-${name}`}>
                        {formatCurrency((row.balances || {})[name])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </section>
    </div>
  );
}

export default Accounts;
